import { useState, useEffect, useCallback, useMemo } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { getMe, logout as apiLogout, startTokenRefresh, stopTokenRefresh } from "@/lib/api";
import { Zap, LayoutDashboard, Package, Users, CalendarDays, FileText, Settings, LogOut, Menu, X, ChevronDown, Shield, DollarSign, Link2, StickyNote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import { Toaster } from "@/components/ui/sonner";

export default function DashboardLayout() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const checkAuth = useCallback(async () => {
    try {
      const res = await getMe();
      setUser(res.data);
      startTokenRefresh();
    } catch {
      stopTokenRefresh();
      navigate("/login", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setLoading(false);
      // Defer replaceState to avoid clearing state before StrictMode re-mount reads it
      setTimeout(() => window.history.replaceState({}, document.title), 0);
      return;
    }
    checkAuth();
  }, [checkAuth, location.state]);

  const handleLogout = async () => {
    stopTokenRefresh();
    await apiLogout();
    navigate("/login", { replace: true });
  };

  const role = user?.role || "crew";
  const outletContext = useMemo(() => ({ user, role }), [user, role]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const roleBadgeClass = (() => {
    if (role === 'owner') return 'role-badge-owner';
    if (role === 'manager') return 'role-badge-manager';
    return 'role-badge-crew';
  })();
  // Crew get a minimal portal: their schedule, their events, their notes.
  const navItems = role === "crew"
    ? [
        { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
        { to: "/events", icon: FileText, label: "My Events" },
        { to: "/calendar", icon: CalendarDays, label: "Calendar" },
        { to: "/notes", icon: StickyNote, label: "Notes" },
      ]
    : [
        { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
        { to: "/inventory", icon: Package, label: "Inventory" },
        { to: "/crew", icon: Users, label: "Crew" },
        { to: "/events", icon: FileText, label: "Events" },
        { to: "/calendar", icon: CalendarDays, label: "Calendar" },
        { to: "/notes", icon: StickyNote, label: "Notes" },
      ];
  if (role === "owner") {
    navItems.push({ to: "/revenue", icon: DollarSign, label: "Revenue" });
    navItems.push({ to: "/team", icon: Shield, label: "Team" });
    navItems.push({ to: "/integrations", icon: Link2, label: "Integrations" });
  }

  const roleLabel = role.charAt(0).toUpperCase() + role.slice(1);

  return (
    <div className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-60 bg-[#0c0c0f] border-r border-border flex flex-col transition-transform duration-200 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex items-center gap-2.5 px-4 h-16 border-b border-border shrink-0">
          <div className="w-8 h-8 rounded-sm bg-primary/10 border border-primary/20 flex items-center justify-center">
            <Zap className="w-4 h-4 text-primary" />
          </div>
          <span className="text-lg font-bold uppercase tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Production Hub
          </span>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase()}`}
              onClick={() => setSidebarOpen(false)}
              className={({ isActive }) => `sidebar-nav-item ${isActive ? 'active' : 'text-muted-foreground'}`}
            >
              <item.icon className="w-4 h-4 shrink-0" />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="px-3 py-4 border-t border-border">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-trigger" className="flex items-center gap-3 w-full px-2 py-2 rounded-sm hover:bg-white/5 transition-colors">
                <Avatar className="w-8 h-8">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-primary/20 text-primary text-xs">{user?.name?.charAt(0)}</AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-sm font-medium truncate">{user?.name}</p>
                  <p className="text-xs text-muted-foreground capitalize">{roleLabel}</p>
                </div>
                <ChevronDown className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem className="text-xs text-muted-foreground" disabled>{user?.email}</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="logout-btn" onClick={handleLogout} className="text-red-400">
                <LogOut className="w-4 h-4 mr-2" /> Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && <div className="fixed inset-0 z-40 bg-black/60 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0 lg:ml-60">
        <header className="sticky top-0 z-30 h-14 border-b border-border bg-background/80 backdrop-blur-sm flex items-center px-4 lg:px-6">
          <Button data-testid="sidebar-toggle" variant="ghost" size="icon" className="lg:hidden mr-2" onClick={() => setSidebarOpen(true)}>
            <Menu className="w-5 h-5" />
          </Button>
          <div className="flex-1" />
          <span className={`text-xs font-semibold uppercase tracking-wider px-2 py-1 rounded-sm border ${roleBadgeClass}`}>
            {roleLabel}
          </span>
        </header>

        <main className="p-4 lg:p-6 overflow-x-hidden">
          <Outlet context={outletContext} />
        </main>
      </div>

      <Toaster theme="dark" />
    </div>
  );
}
